import { useEffect, useState } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { Menu, X, Mountain } from "lucide-react";
import { useT } from "@/i18n/TranslationContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const Header = () => {
  const { t, lang } = useT();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  const base = `/${lang}`;

  const navItems = [
    { href: `${base}#sectors`, label: t("nav.sectors") },
    { href: `${base}#process`, label: t("nav.process") },
    { href: `${base}#transparency`, label: t("nav.transparency") },
    { href: `${base}#contact`, label: t("nav.contact") },
  ];

  // Smooth-scroll to anchor when hash present on home page
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    // wait a tick for DOM
    const tId = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 60);
    return () => clearTimeout(tId);
  }, [location.hash, location.pathname]);

  return (
    <header
      className={`ecominarg-header sticky top-0 z-40 transition-all ${
        scrolled
          ? "bg-white/85 backdrop-blur-md border-b border-[#0b2545]/10 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-4 flex items-center justify-between gap-4">
        <Link
          to={base}
          className="flex items-center gap-2.5 group"
          data-testid="header-logo"
        >
          <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br from-[#0b2545] to-[#13315c] text-white shadow-sm">
            <Mountain className="w-5 h-5" strokeWidth={2} />
            <span className="absolute -bottom-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#c46a3d] ring-2 ring-white" />
          </span>
          <span className="font-extrabold tracking-tight text-[#0b2545] text-lg">
            Ecominarg
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="px-3 py-2 text-sm font-medium text-[#0b2545]/85 hover:text-[#0b2545] rounded-md hover:bg-[#0b2545]/5 transition"
              data-testid={`nav-${item.label}`}
            >
              {item.label}
            </a>
          ))}
          <NavLink
            to={`${base}/privacy-policy`}
            className="px-3 py-2 text-sm font-medium text-[#0b2545]/70 hover:text-[#0b2545] rounded-md hover:bg-[#0b2545]/5 transition"
          >
            {t("footer.privacy")}
          </NavLink>
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <a
            href={`${base}#contact`}
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0b2545] text-white text-sm font-semibold hover:bg-[#13315c] transition"
            data-testid="header-cta"
          >
            {t("hero.cta_primary")}
          </a>
          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-[#0b2545]/15 text-[#0b2545] bg-white/70"
            aria-label={open ? t("nav.close_menu") : t("nav.open_menu")}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            data-testid="header-hamburger"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-300 ease-in-out bg-white border-b border-[#0b2545]/10 ${
          open ? "max-h-[520px]" : "max-h-0"
        }`}
      >
        <div className="px-5 py-4 flex flex-col gap-1">
          {navItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="px-3 py-3 text-base font-medium text-[#0b2545] hover:bg-[#0b2545]/5 rounded-md"
            >
              {item.label}
            </a>
          ))}
          <NavLink
            to={`${base}/privacy-policy`}
            className="px-3 py-3 text-base font-medium text-[#0b2545]/80 hover:bg-[#0b2545]/5 rounded-md"
          >
            {t("footer.privacy")}
          </NavLink>
          <NavLink
            to={`${base}/cookie-policy`}
            className="px-3 py-3 text-base font-medium text-[#0b2545]/80 hover:bg-[#0b2545]/5 rounded-md"
          >
            {t("footer.cookies")}
          </NavLink>
          <NavLink
            to={`${base}/terms-conditions`}
            className="px-3 py-3 text-base font-medium text-[#0b2545]/80 hover:bg-[#0b2545]/5 rounded-md"
          >
            {t("footer.terms")}
          </NavLink>
          <a
            href={`${base}#contact`}
            className="mt-2 inline-flex justify-center items-center px-4 py-3 rounded-full bg-[#0b2545] text-white text-sm font-semibold"
          >
            {t("hero.cta_primary")}
          </a>
        </div>
      </div>
    </header>
  );
};

export default Header;
